import React, { Fragment, useContext, useMemo, useRef, useEffect } from 'react'
import { Button } from 'antd-mobile'
import classnames from 'classnames'
import { ProductItemContext } from '../../context'
import { getBtnStatus, getBtnStatusNew } from '../../utils'
import imgResize from '@/utils/img-resize'
import { BtnStatusMap, SkuInfoItemType, StatusMap, TabItemType } from '../../types'
import { useWithReactFunctionalTrack } from '@du/analytics-sdk'
import { createTrackConfig } from '../../trackConfig'
import styles from './index.less'

function ProgressBar({ remain, total }: { total: number; remain: number }) {
  const per = ((remain / total) * 100).toFixed(0)
  const width = Number.isNaN(per) ? 100 : per
  return (
    <div className={styles['progress-bar-wrapper']}>
      <div
        className={styles['left-bar']}
        style={{
          width: `${width}%`,
        }}
      />
    </div>
  )
}

function getHasBarAndNumber(data: SkuInfoItemType, status: BtnStatusMap, tabItem?: TabItemType) {
  // 文案为去抢购 且 当前会场为抢购中状态
  if (
    status === BtnStatusMap.goBuy &&
    (tabItem?.status === StatusMap.ing || tabItem?.status === StatusMap.end)
  ) {
    return true
  }
  return false
}

function formatMoney(value: any, local?: any): any {
  // TODO move to utils; use enum
  if (local === 'JP' || local === 'KR') {
    return value
  }
  if (value === 0 || value === '0') {
    return 0
  }
  if (!value) {
    return '--'
  }
  return Number(value / 100)
}

const disabledColor = 'rgb(170, 170, 187)'
// const activeColor = 'rgb(215, 50, 51)';

// TODO 状态整理
function ProductItem(props: { data: SkuInfoItemType; $track?: any; index?: number }) {
  const { data } = props
  const {
    moneySymbol,
    btnStatusMap,
    tabItem,
    onRemindStatusChange,
    onGoBuy,
    getRemainingNumber,
    getTotalNumber,
    onProductItemClick,
    pageTitle,
    local,
  } = useContext(ProductItemContext)
  const status = getBtnStatus(data, tabItem)
  const btnMsg = useMemo(() => {
    return btnStatusMap[status]
  }, [status, btnStatusMap])
  const btnDisbaled = useMemo(() => {
    if (!tabItem) {
      return false
    }
    return data.stock === 0 && tabItem.status !== StatusMap.aboutOf
  }, [tabItem, data])

  /** 埋点-开始 */
  const functionalProps = { ...props, record: data, btnMsg, btnStatusMap, tabItem, pageTitle }
  console.log('........functionalProps', functionalProps)
  const functionalRef = useRef(null)
  useWithReactFunctionalTrack({
    functionalRef,
    functionalProps,
    useEffect,
    createTrackConfig,
  })
  /** 埋点-结束 */

  // 活动结束
  const isEnd = tabItem?.status === StatusMap.end
  // 没有仅剩状态条
  // 提醒设置没有状态条
  const isBarAndNumber = getHasBarAndNumber(data, status, tabItem)

  function onClick(e: any) {
    e.stopPropagation()
    if (btnMsg === btnStatusMap.isSetting) {
      onRemindStatusChange(data, false)
    } else if (btnMsg === btnStatusMap.remind) {
      onRemindStatusChange(data, true)
    } else if (btnMsg === btnStatusMap.goBuy) {
      // 去购买
      onGoBuy(data)
    }
  }

  function onPClick() {
    if (tabItem?.status === StatusMap.end) {
      // 活动已结束 不跳转
      return
    }
    onProductItemClick(data)
  }

  const disabledStatus = btnDisbaled || isEnd
  // @ts-ignore
  const colorStyle = disabledStatus ? { color: disabledColor } : {}
  const hasNumber = status === BtnStatusMap.remind || status === BtnStatusMap.isSetting

  return (
    <div
      onClick={onPClick}
      className={classnames({
        [styles['product-item']]: true,
        'product-item-disabled': disabledStatus,
      })}
    >
      <div className={styles['product-img-wrapper']}>
        <img
          className={styles['product-img']}
          data-layzload-src={imgResize(data.imgUrl, 180)}
          alt=""
        />
      </div>
      <div className={styles['product-left-content']}>
        <div style={colorStyle} className={styles['content-title']}>
          {data.title}
        </div>
        <div className={styles['left-bottom-wrapper']}>
          <div className={styles['price-and-property-wrapper']}>
            <div style={colorStyle} className={styles['sku-property']}>
              {data.skuProperty}
            </div>
            <div className={styles['active-price-wrapper']}>
              <span style={colorStyle} className={styles['money-symbol']}>
                {moneySymbol}
              </span>
              <span style={colorStyle} className={styles['active-price-number']}>
                {formatMoney(data.marketingPrice, local)}
              </span>
            </div>
            <div style={colorStyle} className={styles['line-price-wrapper']}>
              {`${moneySymbol}${formatMoney(data.salePrice, local)}`}
            </div>
          </div>
          <div className={styles['snap-up-btn-wrapper']}>
            <div
              className={classnames({
                [styles['snap-up-btn-top']]: true,
                [styles['snap-up-btn-top-isEnd']]: isEnd,
              })}
            >
              {isBarAndNumber && (
                <Fragment>
                  <div className={styles['left-number']}>{getRemainingNumber(data.stock)}</div>
                  <ProgressBar total={data.totStock} remain={data.stock} />
                </Fragment>
              )}
              {hasNumber && (
                <div
                  className={classnames({
                    [styles['left-number']]: true,
                    [styles['left-number-only-number']]: true,
                  })}
                >
                  {getTotalNumber(data.stock || '')}
                </div>
              )}
            </div>
            <div
              ref={functionalRef}
              data-track-click={
                status === BtnStatusMap.remind
                  ? 'poizon_trade_product_purchase_remind_click'
                  : 'poizon_venue_component_content_click'
              }
              data-track-exposure={'poizon_venue_component_content_exposure'}
            >
              <Button
                disabled={disabledStatus}
                onClick={onClick}
                type="primary"
                className={classnames({
                  [styles['product-btn']]: true,
                  [styles['product-setting-btn']]: status === BtnStatusMap.isSetting,
                  [styles['product-remind-btn']]: status === BtnStatusMap.remind,
                })}
              >
                {btnMsg}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ProductList(props: { dataSource: SkuInfoItemType[]; $track?: any }) {
  const { dataSource } = props

  const { tabItem } = useContext(ProductItemContext)

  // 添加埋点
  const functionalPropsRef: React.MutableRefObject<{ $track?: any }> = useRef({})
  const functionalRef = useRef(null)

  useWithReactFunctionalTrack({
    functionalRef,
    functionalProps: functionalPropsRef.current,
    useEffect,
    createTrackConfig,
  })

  const trackHelper = (name, info = {}) => {
    functionalPropsRef.current.$track && functionalPropsRef.current.$track(name, info)
  }

  useEffect(() => {
    observeList()
  }, [])

  const observeList = () => {
    const boxList = [...document.querySelectorAll('.observe-item')]
    // console.log('.....boxList', boxList)
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((item) => {
          // intersectionRatio === 1说明该元素完全暴露出来，符合业务需求
          // console.log(
          //   '........item',
          //   item.target.attributes.getNamedItem('notice-flag').value,
          //   item.target.attributes.getNamedItem('spu-id').value
          // )

          const noticeFlag = item.target.attributes.getNamedItem('notice-flag').value
          const status = getBtnStatusNew(Number(noticeFlag), tabItem)
          if (item.intersectionRatio >= 0.5) {
            // 。。。 埋点曝光代码
            trackHelper('poizon_common_exposure', {
              current_page: 82,
              block_type: 3027,
              spu_id: item.target.attributes.getNamedItem('spu-id').value,
              venue_id: tabItem.venueId,
              status,
            })
            io.unobserve(item.target)
          }
        })
      },
      {
        root: null,
        threshold: [0.5], // 阀值设为1，当只有比例达到1时才触发回调函数
      }
    )
    // observe遍历监听所有box节点
    boxList.forEach((box) => io.observe(box))
  }

  return (
    <div className={styles['product-list-wrapper']} ref={functionalRef}>
      {dataSource.map((ele, index) => {
        return (
          <div
            key={`${ele.skuId}-${ele.biddingNo}`}
            notice-flag={ele.noticeFlag}
            spu-id={ele.spuId}
            className="observe-item"
          >
            <ProductItem index={index} key={`${ele.skuId}-${ele.biddingNo}`} data={ele} />
          </div>
        )
      })}
    </div>
  )
}

export default ProductList
